import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";

type User = {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  role: "admin" | "editor" | "viewer";
  editPermissions: string[] | null; // null = all scopes allowed
  isPublic?: boolean;
};

type AuthContextType = {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  login: () => void;
  publicLogin: (password: string) => Promise<boolean>;
  requestMagicLink: (email: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refetch: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUser = useCallback(async () => {
    try {
      setError(null);
      const response = await fetch("/api/auth/me", {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch user");
      }

      const data = await response.json();
      setUser(data.user);
    } catch (err) {
      console.error("Auth fetch error:", err);
      setError(err instanceof Error ? err.message : "Authentication error");
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = useCallback(() => {
    window.location.href = "/api/auth/google";
  }, []);

  const publicLogin = useCallback(async (password: string): Promise<boolean> => {
    try {
      setError(null);
      const response = await fetch("/api/auth/public", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || "Invalid password");
        return false;
      }

      // Refetch user to get the public user data
      await fetchUser();
      return true;
    } catch (err) {
      console.error("Public login error:", err);
      setError("Failed to login");
      return false;
    }
  }, [fetchUser]);

  const requestMagicLink = useCallback(async (email: string): Promise<boolean> => {
    try {
      setError(null);
      const response = await fetch("/api/auth/magic-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        setError("Failed to send login link");
        return false;
      }

      return true;
    } catch (err) {
      console.error("Magic link error:", err);
      setError("Failed to send login link");
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
      setUser(null);
    } catch (err) {
      console.error("Logout error:", err);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        error,
        login,
        publicLogin,
        requestMagicLink,
        logout,
        refetch: fetchUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
